import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetProjectsQuery } from '@/store/api/apiSlice';
import { useProjectContext } from '@/context/ProjectContext';
import { normalizeProjectList } from '@/utils/projectList';
import ProjectCreationModal from '@/components/projects/ProjectCreationModal';

const PROJECT_COLORS = [
  '#1D4ED8', '#0F766E', '#7C3AED', '#B45309',
  '#B91C1C', '#0369A1', '#9D174D', '#4338CA',
];

function getProjectColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
}

function getStatusDot(status: string) {
  switch (status) {
    case 'ACTIVE':    return 'bg-green-500';
    case 'PLANNING':  return 'bg-blue-400';
    case 'ON_HOLD':   return 'bg-amber-400';
    case 'COMPLETED': return 'bg-gray-400';
    case 'ARCHIVED':  return 'bg-gray-300';
    default:          return 'bg-gray-300';
  }
}

interface Props {
  isCollapsed: boolean;
}

export default function ProjectSelector({ isCollapsed }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const selectorRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { selectedProjectId, setSelectedProjectId, defaultProjectId, setDefaultProjectId } = useProjectContext();
  const { data: projectsData, refetch } = useGetProjectsQuery({ limit: 100 });

  const projects: any[] = normalizeProjectList(projectsData);

  // Guarantee at least one project is always selected.
  useEffect(() => {
    if (projects.length === 0) return;
    const stillExists = (id: string | null) => !!projects.find((p) => p.id === id);
    if (selectedProjectId && stillExists(selectedProjectId)) return;
    const fallback = (defaultProjectId && stillExists(defaultProjectId))
      ? defaultProjectId
      : projects[0].id;
    setSelectedProjectId(fallback);
  }, [projects.length, selectedProjectId, defaultProjectId, setSelectedProjectId]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId) ?? projects[0] ?? null;
  const filteredProjects = search.trim()
    ? projects.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : projects;

  const openDropdown = () => {
    if (!selectorRef.current) return;
    const rect = selectorRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 248),
      zIndex: 9999,
    });
    setOpen(true);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 30);
  };

  const closeDropdown = () => {
    setOpen(false);
    setSearch('');
  };

  const handleSelect = (project: any) => {
    setSelectedProjectId(project.id);
    closeDropdown();
  };

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const portal = document.getElementById('ps-portal');
      if (
        !selectorRef.current?.contains(e.target as Node) &&
        !portal?.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDropdown(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  const letter = selectedProject?.name?.[0]?.toUpperCase() ?? '?';

  const dropdown = open ? (
    <div id="ps-portal" style={dropdownStyle}>
      <div
        className="bg-surface border border-border rounded-xl shadow-xl overflow-hidden"
        style={{ boxShadow: '0 8px 30px rgba(0,0,0,.13)' }}
      >
        <div className="px-2.5 py-2 border-b border-border">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-bg border border-border focus-within:border-brand transition-colors">
            <svg className="w-3.5 h-3.5 shrink-0 fill-none stroke-current text-text-muted" strokeWidth={2} strokeLinecap="round" viewBox="0 0 16 16">
              <circle cx="6.5" cy="6.5" r="4.5" />
              <path d="M14 14l-3-3" />
            </svg>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('project.search')}
              className="flex-1 bg-transparent outline-none text-[12px] text-text-primary placeholder-text-muted min-w-0"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-text-muted hover:text-text-primary text-xs">✕</button>
            )}
          </div>
        </div>

        <div className="max-h-[260px] overflow-y-auto scrollbar-thin py-1">
          {filteredProjects.length === 0 && (
            <div className="px-4 py-6 text-center text-[11px] text-text-muted">{t('project.noneFound')}</div>
          )}
          {filteredProjects.map((project: any) => {
            const isSelected = project.id === selectedProjectId;
            const taskCount = project._count?.tasks ?? 0;
            const sprintCount = project._count?.sprints ?? 0;
            return (
              <button
                key={project.id}
                onClick={() => handleSelect(project)}
                className={`group w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  isSelected ? 'bg-brand/10' : 'hover:bg-surface-hover'
                }`}
              >
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0"
                  style={{ background: 'var(--project-icon-bg)', color: 'var(--project-icon-text)' }}
                >
                  {project.name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[12px] font-semibold truncate ${isSelected ? 'text-brand' : 'text-text-primary'}`}>
                      {project.name}
                    </span>
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${getStatusDot(project.status)}`} />
                  </div>
                  <div className="text-[11px] text-text-muted flex items-center gap-2 mt-0.5">
                    <span>{t('project.taskCount', { count: taskCount })}</span>
                    {sprintCount > 0 && <><span className="text-border">·</span><span>{t('project.sprintCount', { count: sprintCount })}</span></>}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setDefaultProjectId(project.id); }}
                  title={project.id === defaultProjectId ? t('project.defaultProject') : t('project.setDefault')}
                  className={`ml-auto shrink-0 p-0.5 rounded transition-colors ${
                    project.id === defaultProjectId
                      ? 'text-amber-400'
                      : 'text-transparent hover:text-text-muted group-hover:text-text-muted'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill={project.id === defaultProjectId ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 1l1.9 3.9L14 5.6l-3 2.9.7 4.1L8 10.5l-3.7 2.1.7-4.1-3-2.9 4.1-.7z" />
                  </svg>
                </button>
                {isSelected && (
                  <svg className="w-4 h-4 text-brand shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 16 16">
                    <path d="M2 8l4 4 8-8" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        <div className="border-t border-border px-1 py-1">
          <button
            onClick={() => {
              closeDropdown();
              setCreateOpen(true);
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium text-text-secondary hover:bg-surface-hover hover:text-brand transition-colors"
          >
            <div className="w-5 h-5 rounded-md border-2 border-dashed border-current flex items-center justify-center shrink-0">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 12 12">
                <path d="M6 2v8M2 6h8" />
              </svg>
            </div>
            {t('project.createNew')}
          </button>
          <button
            onClick={() => { closeDropdown(); navigate('/projects'); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium text-text-muted hover:bg-surface-hover transition-colors"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" viewBox="0 0 18 18">
              <rect x="2" y="2" width="6" height="6" rx="1.5" />
              <rect x="10" y="2" width="6" height="6" rx="1.5" />
              <rect x="2" y="10" width="6" height="6" rx="1.5" />
              <rect x="10" y="10" width="6" height="6" rx="1.5" />
            </svg>
            {t('project.allProjects')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={selectorRef}
        onClick={open ? closeDropdown : openDropdown}
        className={`mx-2 mt-1.5 mb-0.5 rounded-[10px] cursor-pointer select-none transition-all ${
          open ? 'bg-brand/8' : 'hover:bg-surface-hover'
        }`}
        style={{ border: 'none' }}
        title={isCollapsed ? (selectedProject?.name ?? t('project.select')) : undefined}
      >
        {isCollapsed ? (
          <div className="flex items-center justify-center p-1.5">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-[14px] font-bold"
              style={{ background: 'var(--project-icon-bg)', color: 'var(--project-icon-text)' }}
            >
              {letter}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 px-3 py-2">
            <div
              className="w-7 h-7 rounded-[8px] flex items-center justify-center text-[12px] font-bold shrink-0"
              style={{ background: 'var(--project-icon-bg)', color: 'var(--project-icon-text)' }}
            >
              {letter}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold tracking-[.06em] uppercase text-text-muted leading-none mb-0.5">
                {t('project.current')}
              </div>
              <div className="text-[12px] font-semibold text-text-primary truncate leading-tight">
                {selectedProject?.name ?? t('project.select')}
              </div>
            </div>
            <svg
              className={`w-3.5 h-3.5 shrink-0 stroke-current fill-none text-text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              strokeWidth={2}
              strokeLinecap="round"
              viewBox="0 0 14 14"
            >
              <path d="M2 5l5 5 5-5" />
            </svg>
          </div>
        )}
      </div>

      {open && createPortal(dropdown, document.body)}

      <ProjectCreationModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          refetch();
          setCreateOpen(false);
        }}
      />
    </>
  );
}
